import { useState } from "react";
import NumberInput from "../shared/NumberInput";
import AddressAutocomplete from "../shared/AddressAutocomplete";
import BudgetSlider from "../shared/BudgetSlider";
import StepNavigation from "../shared/StepNavigation";
import { FoerderFormData } from "./FoerderRechner";
import clsx from "clsx";

interface Props {
  data: FoerderFormData;
  onChange: (data: Partial<FoerderFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function Step3Details({ data, onChange, onNext, onBack }: Props) {
  const [errors, setErrors] = useState<{
    eigentuemer?: string;
    postleitzahl?: string;
    investitionskosten?: string;
  }>({});

  const heizungsMassnahmen = ["waermepumpe", "biomasse", "solarthermie"];
  const isHeizung = (data.massnahmen ?? []).some((m) => heizungsMassnahmen.includes(m));

  const validate = () => {
    const e: typeof errors = {};
    if (data.eigentuemer === undefined) e.eigentuemer = "Bitte auswählen.";
    if (!data.postleitzahl || !/^\d{5}$/.test(data.postleitzahl))
      e.postleitzahl = "Bitte gültige Postleitzahl eingeben (5 Ziffern).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div className="animate-slide-up" data-testid="step-3-details">
      {/* Eigentümer */}
      <div className="mb-4">
        <p className="text-sm font-medium text-neko-gray-light mb-2">Sind Sie Eigentümer?</p>
        <div className="flex gap-2">
          {[
            { label: "Ja, Eigentümer", value: true },
            { label: "Nein, Mieter", value: false },
          ].map(({ label, value }) => (
            <button
              key={String(value)}
              type="button"
              onClick={() => onChange({ eigentuemer: value })}
              className={clsx(
                "flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-all",
                data.eigentuemer === value
                  ? "border-neko-orange bg-neko-orange/10 text-neko-white"
                  : "border-neko-dark-3 bg-neko-dark-2 text-neko-gray hover:border-neko-gray"
              )}
              data-testid={`eigentuemer-${String(value)}`}
            >
              {label}
            </button>
          ))}
        </div>
        {errors.eigentuemer && <p className="text-xs text-neko-red mt-1" role="alert">{errors.eigentuemer}</p>}
        {data.eigentuemer === false && (
          <p className="text-xs text-neko-orange mt-2 bg-neko-orange/10 rounded-lg p-2">
            Hinweis: Als Mieter haben Sie keinen Anspruch auf KfW-Förderung. Sprechen Sie mit Ihrem Vermieter.
          </p>
        )}
      </div>

      {/* Adresse / PLZ */}
      <AddressAutocomplete
        label="Postleitzahl / Adresse"
        onPlzSelect={(plz) => onChange({ postleitzahl: plz })}
        error={errors.postleitzahl}
        hint="Adresse eingeben → PLZ wird automatisch übernommen. Wird für regionale Zusatzförderungen genutzt."
      />

      {/* Investitionskosten */}
      <BudgetSlider
        label="Geplante Investitionskosten"
        value={data.investitionskosten}
        onChange={(v) => onChange({ investitionskosten: v })}
        min={5000}
        max={100000}
        step={1000}
        optional
        hint="Für eine genauere Euro-Berechnung."
      />

      {/* Haushaltseinkommen (nur für KfW 458) */}
      {isHeizung && (
        <NumberInput
          label="Zu versteuerndes Haushaltseinkommen"
          placeholder="z. B. 38000"
          unit="€/Jahr"
          min={0}
          optional
          value={data.haushaltseinkommen ?? ""}
          onChange={(e) => onChange({ haushaltseinkommen: parseFloat(e.target.value) || undefined })}
          hint="Für Einkommensbonus bei unter 40.000 € relevant."
          data-testid="einkommen-input"
        />
      )}

      {/* Klimageschwindigkeitsbonus-Checkbox (nur Heizung) */}
      {isHeizung && (
        <label className="flex items-start gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={data.fossile_heizung_vorhanden ?? false}
            onChange={(e) => onChange({ fossile_heizung_vorhanden: e.target.checked })}
            className="mt-0.5 w-4 h-4 rounded border-neko-dark-3 accent-neko-orange"
            data-testid="fossile-heizung-checkbox"
          />
          <span className="text-sm text-neko-gray-light">
            Ich tausche eine funktionierende Gas- oder Ölheizung aus
            <span className="block text-xs text-neko-gray mt-0.5">(+20% Klimageschwindigkeitsbonus möglich)</span>
          </span>
        </label>
      )}

      {/* iSFP-Checkbox */}
      <label className="flex items-start gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={data.isfp_vorhanden ?? false}
          onChange={(e) => onChange({ isfp_vorhanden: e.target.checked })}
          className="mt-0.5 w-4 h-4 rounded border-neko-dark-3 accent-neko-orange"
          data-testid="isfp-checkbox"
        />
        <span className="text-sm text-neko-gray-light">
          Ich habe einen individuellen Sanierungsfahrplan (iSFP)
          <span className="block text-xs text-neko-gray mt-0.5">(+5% iSFP-Bonus möglich)</span>
        </span>
      </label>

      <StepNavigation
        onBack={onBack}
        onNext={() => { if (validate()) onNext(); }}
        nextLabel="Förderung berechnen"
        isLastStep
      />
    </div>
  );
}
